import { useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Loader2, Zap, UserCheck, ArrowLeft, Sparkles, LayoutTemplate } from "lucide-react";
import { LeadPicker, TemplatePicker, StylePicker, CadenceNote } from "./CampaignCreateSections";

const Section = ({ label, icon: Icon, hint, testId, children }) => (
  <div className="glass-card p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-ism-teal" />}
      <label className="text-sm text-gray-400">{label}</label>
    </div>
    {hint && <p className="text-xs text-gray-600">{hint}</p>}
    {children}
  </div>
);

const SourceButton = ({ active, onClick, icon: Icon, testId, children }) => (
  <Button onClick={onClick} variant={active ? "default" : "outline"} size="sm" className={active ? "bg-ism-teal text-black" : "border-white/10 text-gray-400"} data-testid={testId}>
    <Icon className="h-4 w-4 mr-1" /> {children}
  </Button>
);

export const CampaignCreate = ({ userId, templates, onBack, onCreateCSV, onCreateLeads, onDeleteTemplate }) => {
  const [createMode, setCreateMode] = useState("csv");
  const [campaignName, setCampaignName] = useState("");
  const [cadenceStyle, setCadenceStyle] = useState("consultative");
  const [exemplarEmail, setExemplarEmail] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const fileInputRef = useRef(null);

  const selectedTemplate = templates.find((t) => t.templateId === selectedTemplateId);

  const chooseLeadsMode = async () => {
    setCreateMode("leads");
    setLeadsLoading(true);
    try {
      const res = await axios.get(`${API}/leads/${userId}`);
      setLeads(Array.isArray(res.data) ? res.data : (res.data.leads || []));
    } catch (e) {
      console.error("Error loading leads:", e);
    } finally {
      setLeadsLoading(false);
    }
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplateId(tpl?.templateId || "");
    if (tpl?.cadenceStyle) setCadenceStyle(tpl.cadenceStyle);
  };

  const removeTemplate = (templateId) => {
    if (selectedTemplateId === templateId) setSelectedTemplateId("");
    onDeleteTemplate(templateId);
  };

  const toggleLead = (leadId) =>
    setSelectedLeadIds((prev) => (prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]));
  const toggleAllLeads = () =>
    setSelectedLeadIds(selectedLeadIds.length === leads.length ? [] : leads.map((l) => l.leadId));

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("campaignName", campaignName || file.name.replace(/\.[^.]+$/, ""));
    formData.append("cadenceStyle", cadenceStyle);
    formData.append("exemplarEmail", exemplarEmail);
    formData.append("templateId", selectedTemplateId || "");
    await onCreateCSV(formData);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCreateFromLeads = async () => {
    if (selectedLeadIds.length === 0) return toast.error("Select at least one lead");
    setUploading(true);
    await onCreateLeads({
      campaignName: campaignName || "Campaign from Leads",
      leadIds: selectedLeadIds,
      cadenceStyle,
      exemplarEmail: exemplarEmail || null,
      templateId: selectedTemplateId || null,
    });
    setUploading(false);
  };

  return (
    <div className="space-y-6" data-testid="campaign-create">
      <div className="flex items-center gap-3">
        <Button onClick={onBack} variant="ghost" size="sm" className="text-gray-400 hover:text-white" data-testid="create-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl text-white font-semibold">New Outreach Campaign</h1>
      </div>

      <Section label="Campaign Name">
        <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g., Q1 Enterprise Outreach" className="bg-white/5 border-white/10 text-white" data-testid="campaign-name-input" />
      </Section>

      <Section label="Prospect Source">
        <div className="flex gap-2">
          <SourceButton active={createMode === "csv"} onClick={() => setCreateMode("csv")} icon={Upload} testId="source-csv-btn">Upload CSV</SourceButton>
          <SourceButton active={createMode === "leads"} onClick={chooseLeadsMode} icon={UserCheck} testId="source-leads-btn">Select from CRM</SourceButton>
        </div>
        {createMode === "csv" ? (
          <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-ism-teal/30 transition-colors" onClick={() => fileInputRef.current?.click()} data-testid="csv-dropzone">
            {uploading ? <Loader2 className="h-8 w-8 mx-auto text-ism-teal mb-2 animate-spin" /> : <Upload className="h-8 w-8 mx-auto text-gray-500 mb-2" />}
            <p className="text-sm text-gray-400">{uploading ? "Importing prospects..." : "Click to upload a CSV with prospect data"}</p>
            <p className="text-xs text-gray-600 mt-1">Columns: Name, Company, Title, Email (auto-detected)</p>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleCSVUpload} />
          </div>
        ) : (
          <LeadPicker leads={leads} loading={leadsLoading} selectedIds={selectedLeadIds} onToggle={toggleLead} onToggleAll={toggleAllLeads} />
        )}
      </Section>

      <Section label="Cadence Template (optional)" icon={LayoutTemplate} testId="template-picker"
        hint="Reuse a saved cadence structure (steps, timing & style). Leave on “AI-determined” to let ISM design the cadence for you.">
        <TemplatePicker templates={templates} selectedId={selectedTemplateId} onSelect={selectTemplate} onDelete={removeTemplate} />
      </Section>

      <Section label="Outreach Style">
        <StylePicker value={cadenceStyle} onChange={setCadenceStyle} />
      </Section>

      <Section label="Exemplar Email (optional)" icon={Sparkles}
        hint="Paste an email you've written that worked well. The AI will model its tone, structure, and style.">
        <Textarea value={exemplarEmail} onChange={(e) => setExemplarEmail(e.target.value)} placeholder="Paste a real email you've sent that got a positive response..." rows={5} className="bg-white/5 border-white/10 text-white text-sm resize-none" data-testid="exemplar-input" />
      </Section>

      <CadenceNote template={selectedTemplate} />

      {createMode === "leads" && (
        <Button onClick={handleCreateFromLeads} disabled={uploading || selectedLeadIds.length === 0} className="w-full bg-ism-teal hover:bg-ism-teal/90 text-black font-medium py-3" data-testid="launch-campaign-btn">
          {uploading
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating Campaign...</>
            : <><Zap className="h-4 w-4 mr-2" /> Launch Campaign ({selectedLeadIds.length} {selectedLeadIds.length === 1 ? "prospect" : "prospects"})</>}
        </Button>
      )}
    </div>
  );
};
